import { useEffect, useState, type RefObject } from 'react';

interface UseHorizontalOverflowIndicatorsParams {
  ref: RefObject<HTMLElement | null>;
  threshold?: number;
  watchValue?: unknown;
}

export const useHorizontalOverflowIndicators = ({
  ref,
  threshold = 8,
  watchValue,
}: UseHorizontalOverflowIndicatorsParams) => {
  const [showLeftOverflow, setShowLeftOverflow] = useState(false);
  const [showRightOverflow, setShowRightOverflow] = useState(false);

  useEffect(() => {
    const container = ref.current;

    if (!container) return;

    const updateOverflowState = () => {
      const { scrollLeft, scrollWidth, clientWidth } = container;
      const maxScrollLeft = Math.max(0, scrollWidth - clientWidth);

      setShowLeftOverflow(scrollLeft > threshold);
      setShowRightOverflow(maxScrollLeft - scrollLeft > threshold);
    };

    updateOverflowState();

    container.addEventListener('scroll', updateOverflowState, {
      passive: true,
    });
    window.addEventListener('resize', updateOverflowState);

    return () => {
      container.removeEventListener('scroll', updateOverflowState);
      window.removeEventListener('resize', updateOverflowState);
    };
  }, [ref, threshold, watchValue]);

  return { showLeftOverflow, showRightOverflow };
};
